import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Card } from '../../../components/ui/card';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { Alert, Snackbar } from '@mui/material';
import {
	buildLogbookSalesMetricPreferenceMap,
	fetchLogbookSalesMetricPreferences,
	fetchLogbookSalesMetrics,
	LogbookSalesMetric,
	upsertLogbookSalesMetricPreference,
} from 'core/API/logbookSalesMetrics';
import styles from './SchedulerSetting.module.scss';

type MetricRow = LogbookSalesMetric & {
	preferenceExists: boolean;
	preferenceActive: boolean | null;
	checked: boolean;
};

const LogbooksSetting = () => {
	const queryClient = useQueryClient();
	const { workplaceId } = useWorkplacesContext();
	const workplaceIdNum = Number(workplaceId);
	const [snackbar, setSnackbar] = useState<{
		open: boolean;
		message: string;
		severity: 'success' | 'error' | 'info' | 'warning';
	}>({
		open: false,
		message: '',
		severity: 'success',
	});

	const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

	const {
		data: masterMetrics,
		isLoading: isMasterLoading,
		isError: isMasterError,
	} = useQuery(['logbook-sales-metrics'], fetchLogbookSalesMetrics, {
		enabled: true,
		retry: false,
		refetchOnWindowFocus: false,
		staleTime: 5 * 60 * 1000,
	});

	const {
		data: preferences,
		isLoading: isPreferencesLoading,
		isError: isPreferencesError,
	} = useQuery(
		['logbook-sales-metric-preferences', workplaceIdNum],
		() => fetchLogbookSalesMetricPreferences(workplaceIdNum),
		{
			enabled: !!workplaceIdNum,
			retry: false,
			refetchOnWindowFocus: false,
		},
	);

	const derivedRows = useMemo<MetricRow[]>(() => {
		if (!masterMetrics) return [];

		const prefMap = buildLogbookSalesMetricPreferenceMap(preferences);

		// Preserve API order exactly (no client-side sorting/grouping).
		return masterMetrics
			.map((m) => {
				const pref = prefMap.get(m.id);
				const preferenceExists = pref != null;
				const preferenceActive = preferenceExists ? pref!.active : null;
				const checked = preferenceExists ? Boolean(preferenceActive) : false;
				return {
					...m,
					preferenceExists,
					preferenceActive,
					checked,
				};
			});
	}, [masterMetrics, preferences]);

	const [rows, setRows] = useState<MetricRow[]>([]);
	useEffect(() => {
		setRows(derivedRows);
	}, [derivedRows]);

	const categories = useMemo(() => {
		// Preserve first-seen category order as returned by the API.
		const seen = new Set<string>();
		const ordered: string[] = [];
		rows.forEach((r) => {
			const cat = r.category ?? '';
			if (!seen.has(cat)) {
				seen.add(cat);
				ordered.push(cat);
			}
		});
		return ordered;
	}, [rows]);

	const { mutateAsync: savePreference, isLoading: isSaving } = useMutation({
		mutationFn: async (args: { metricId: number; active: boolean }) => {
			if (!workplaceIdNum) throw new Error('Workplace is not selected.');
			return upsertLogbookSalesMetricPreference({
				workplaceId: workplaceIdNum,
				metricId: args.metricId,
				active: args.active,
			});
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries(['logbook-sales-metric-preferences', workplaceIdNum]);
			setSnackbar({
				open: true,
				message: 'Metric preference saved',
				severity: 'success',
			});
		},
		onError: (e: any) => {
			console.error('Failed to save metric preference', e);
			setSnackbar({
				open: true,
				message: e?.message || 'Failed to save metric preference',
				severity: 'error',
			});
		},
	});

	const [savingMetricId, setSavingMetricId] = useState<number | null>(null);

	const toggleActive = async (metricId: number) => {
		if (!rows.length) return;
		const current = rows.find((r) => r.id === metricId);
		if (!current) return;

		// checked means "preference exists AND active=true"
		const currentlyChecked = current.checked;
		const nextActive = !currentlyChecked;

		const previousRows = rows;
		setSavingMetricId(metricId);
		setRows((prev) =>
			prev.map((r) =>
				r.id === metricId
					? {
							...r,
							preferenceExists: true,
							preferenceActive: nextActive,
							checked: nextActive,
						}
					: r,
			),
		);

		try {
			await savePreference({ metricId, active: nextActive });
		} catch {
			// revert optimistic state
			setRows(previousRows);
		} finally {
			setSavingMetricId(null);
		}
	};

	const isBusy = isMasterLoading || (isPreferencesLoading && !!workplaceIdNum);

	return (
		<div className={styles.schedulerSettings}>
			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Logbook Sale Metrics</h2>
				</div>
				<div className={styles.list}>
					<div className={styles.tableContainer}>
						<table className={styles.workplaceTable}>
							<thead>
								<tr>
									<th>Metric</th>
									<th style={{ width: 160, textAlign: 'center' }}>Active</th>
								</tr>
							</thead>
							<tbody>
								{isBusy ? (
									<tr>
										<td
											colSpan={2}
											style={{ textAlign: 'center', padding: '20px', color: '#666' }}
										>
											Loading...
										</td>
									</tr>
								) : isMasterError || isPreferencesError ? (
									<tr>
										<td
											colSpan={2}
											style={{ textAlign: 'center', padding: '20px', color: '#666' }}
										>
											Failed to load metrics
										</td>
									</tr>
								) : rows.length === 0 ? (
									<tr>
										<td
											colSpan={2}
											style={{ textAlign: 'center', padding: '20px', color: '#666' }}
										>
											No records found
										</td>
									</tr>
								) : (
									categories.map((cat) => {
										const catRows = rows
											.filter((r) => r.category === cat)
											;
										return (
											<React.Fragment key={cat}>
												<tr>
													<td
														colSpan={2}
														style={{
															fontWeight: 700,
															color: '#446388',
															backgroundColor: '#f5f7fb',
															padding: '10px 12px',
														}}
													>
														{cat}
													</td>
												</tr>
												{catRows.map((r) => {
													// Only show text when active; never show an "Inactive" label
													const statusText =
														r.preferenceExists && r.preferenceActive === true ? 'Active' : '';
													return (
														<tr key={r.id}>
															<td>{r.name}</td>
															<td style={{ textAlign: 'center' }}>
																<label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
																	<input
																		type="checkbox"
																		checked={r.checked}
																		disabled={
																			isSaving && savingMetricId === r.id
																		}
																		onChange={() => toggleActive(r.id)}
																		title={
																			r.checked ? 'Set as Inactive' : 'Set as Active'
																		}
																	/>
																	<span>{statusText}</span>
																</label>
															</td>
														</tr>
													);
												})}
											</React.Fragment>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</div>
			</Card>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default LogbooksSetting;